// src/utils/redditPixel.js - Reddit Pixel Tracking Utility
// GDPR Compliant with robust error handling and cleanup

export const REDDIT_PIXEL_ID = process.env.REACT_APP_REDDIT_PIXEL_ID || 'a2_hv2bzc1258iw';

let isCurrentUserAdmin = false;
let isInitialized = false;
let scriptLoaded = false;
let initAttempted = false; // Prevent repeated failed attempts

export const setUserAdminStatus = (isAdmin) => {
  isCurrentUserAdmin = isAdmin;
};

// Helper function to check if analytics consent is given
const hasAnalyticsConsent = () => {
  const consent = localStorage.getItem('cookieConsent');
  if (!consent) return false;
  
  try {
    const parsedConsent = JSON.parse(consent);
    return parsedConsent.analytics === true;
  } catch (error) {
    console.error('Error parsing cookie consent:', error);
    return false;
  }
};

// Helper function to check if we should track
const shouldTrack = () => {
  const hasConsent = hasAnalyticsConsent();
  const isProductionOrEnabled = process.env.NODE_ENV === 'production' || 
                                process.env.REACT_APP_ENABLE_REDDIT_PIXEL === 'true';

  if (isCurrentUserAdmin) {
    return false;
  }
  
  return hasConsent && isProductionOrEnabled && REDDIT_PIXEL_ID;
};

// Clean up any existing Reddit Pixel instances
const cleanupExistingPixel = () => {
  try {
    // Remove any existing rdt function to prevent conflicts
    if (window.rdt) {
      console.log('🧹 Cleaning up existing Reddit Pixel instance');
      delete window.rdt;
    }
    
    // Remove existing script tag
    const existingScripts = document.querySelectorAll('script[src*="redditstatic.com"]');
    existingScripts.forEach(script => {
      console.log('🧹 Removing existing Reddit Pixel script');
      script.remove();
    });
    
    scriptLoaded = false;
    isInitialized = false;
  } catch (error) {
    console.error('Error cleaning up Reddit Pixel:', error);
  }
};

// Initialize Reddit Pixel with robust error handling
export const initRedditPixel = async () => {
  // Prevent multiple initialization attempts
  if (initAttempted) {
    console.log('⚠️ Reddit Pixel initialization already attempted');
    return;
  }

  // Check if already initialized
  if (isInitialized && window.rdt) {
    console.log('📊 Reddit Pixel already initialized');
    return;
  }

  if (!shouldTrack()) {
    if (isCurrentUserAdmin) {
      console.log('📊 Reddit Pixel disabled - admin user');
    } else if (!hasAnalyticsConsent()) {
      console.log('📊 Reddit Pixel disabled - no user consent');
    } else if (!REDDIT_PIXEL_ID) {
      console.log('📊 Reddit Pixel disabled - no Pixel ID configured');
    } else {
      console.log('📊 Reddit Pixel disabled - not in production mode');
    }
    return;
  }

  initAttempted = true;

  try {
    console.log('🔄 Starting Reddit Pixel initialization...');
    
    // Clean up any existing instances first
    cleanupExistingPixel();
    
    // Wait a moment for cleanup
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Create the Reddit Pixel loader function inline (like their snippet)
    console.log('📝 Creating Reddit Pixel loader...');
    
    // eslint-disable-next-line no-unused-expressions
    !function(w,d){
      if(!w.rdt){
        var p=w.rdt=function(){
          p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)
        };
        p.callQueue=[];
        
        var t=d.createElement("script");
        t.src="https://www.redditstatic.com/ads/pixel.js";
        t.async=!0;
        
        t.onload = function() {
          console.log('✅ Reddit Pixel script loaded successfully');
        };
        
        t.onerror = function(error) {
          console.error('❌ Failed to load Reddit Pixel script:', error);
        };
        
        var s=d.getElementsByTagName("script")[0];
        s.parentNode.insertBefore(t,s);
      }
    }(window,document);
    
    scriptLoaded = true;
    
    // Wait for script to load and initialize
    let attempts = 0;
    const maxAttempts = 20; // 2 seconds max wait
    
    while (!window.rdt && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (window.rdt && typeof window.rdt === 'function') {
      console.log('✅ window.rdt is available');
      
      // Initialize with pixel ID
      try {
        window.rdt('init', REDDIT_PIXEL_ID, {
          optOut: false,
          useDecimalCurrencyValues: true
        });
        
        isInitialized = true;
        console.log('📊 Reddit Pixel initialized with ID:', REDDIT_PIXEL_ID);
        
        // Track initial page visit
        window.rdt('track', 'PageVisit');
        console.log('📊 Initial PageVisit tracked');
        
      } catch (initError) {
        console.error('❌ Error calling rdt init:', initError);
        isInitialized = false;
      }
    } else {
      console.error('❌ window.rdt not available after waiting');
      isInitialized = false;
    }
    
  } catch (error) {
    console.error('❌ Error initializing Reddit Pixel:', error);
    isInitialized = false;
  }
};

// Track page views
export const trackRedditPageView = () => {
  if (!isInitialized || !window.rdt) {
    return; // Silently skip if not initialized
  }

  if (shouldTrack()) {
    try {
      window.rdt('track', 'PageVisit');
      console.log('📊 Reddit PageVisit tracked');
    } catch (error) {
      console.error('❌ Error tracking PageVisit:', error);
    }
  }
};

// Track signup/registration event
export const trackRedditSignup = (additionalData = {}) => {
  if (!isInitialized || !window.rdt) {
    console.log('⚠️ Reddit SignUp not tracked - pixel not initialized');
    return;
  }

  if (shouldTrack()) {
    try {
      window.rdt('track', 'SignUp', additionalData);
      console.log('📊 Reddit SignUp event tracked', additionalData);
    } catch (error) {
      console.error('❌ Error tracking SignUp:', error);
    }
  }
};

// Track lead generation
export const trackRedditLead = (additionalData = {}) => {
  if (!isInitialized || !window.rdt) return;
  
  if (shouldTrack()) {
    try {
      window.rdt('track', 'Lead', additionalData);
      console.log('📊 Reddit Lead event tracked', additionalData);
    } catch (error) {
      console.error('❌ Error tracking Lead:', error);
    }
  }
};

// Track purchase/conversion
export const trackRedditPurchase = (value, currency = 'GBP', additionalData = {}) => {
  if (!isInitialized || !window.rdt) return;
  
  if (shouldTrack()) {
    try {
      window.rdt('track', 'Purchase', {
        value: value,
        currency: currency,
        ...additionalData
      });
      console.log('📊 Reddit Purchase event tracked', { value, currency, ...additionalData });
    } catch (error) {
      console.error('❌ Error tracking Purchase:', error);
    }
  }
};

// Track custom events
export const trackRedditCustomEvent = (eventName, eventData = {}) => {
  if (!isInitialized || !window.rdt) return;
  
  if (shouldTrack()) {
    try {
      window.rdt('track', 'Custom', {
        customEventName: eventName,
        ...eventData
      });
      console.log('📊 Reddit Custom event tracked:', eventName, eventData);
    } catch (error) {
      console.error('❌ Error tracking Custom event:', error);
    }
  }
};

// Track offer views
export const trackRedditOfferView = (offerId, offerTitle) => {
  if (!isInitialized || !window.rdt) return;
  
  if (shouldTrack()) {
    try {
      window.rdt('track', 'ViewContent', {
        itemCount: 1,
        contents: [{
          id: offerId,
          name: offerTitle
        }]
      });
      console.log('📊 Reddit ViewContent (Offer) tracked:', offerId);
    } catch (error) {
      console.error('❌ Error tracking ViewContent:', error);
    }
  }
};

// Update advanced matching
export const updateRedditUserMatch = (userId, email = null) => {
  if (!isInitialized || !window.rdt) return;
  
  if (shouldTrack() && userId) {
    try {
      const advancedMatching = {
        externalId: userId
      };
      
      if (email) {
        advancedMatching.email = email;
      }
      
      window.rdt('init', REDDIT_PIXEL_ID, advancedMatching);
      console.log('📊 Reddit Pixel updated with user matching:', userId);
    } catch (error) {
      console.error('❌ Error updating user match:', error);
    }
  }
};

// Disable Reddit Pixel
export const disableRedditPixel = () => {
  console.log('🛑 Disabling Reddit Pixel...');
  
  if (window.rdt && typeof window.rdt === 'function') {
    try {
      window.rdt('disableFirstPartyCookies');
      console.log('📊 Reddit Pixel first-party cookies disabled');
    } catch (error) {
      console.error('❌ Error disabling Reddit Pixel:', error);
    }
  }
  
  // Clean up completely
  cleanupExistingPixel();
  initAttempted = false; // Allow re-initialization if user opts back in
  
  console.log('✅ Reddit Pixel cleanup complete');
};

export default {
  initRedditPixel,
  trackRedditPageView,
  trackRedditSignup,
  trackRedditLead,
  trackRedditPurchase,
  trackRedditCustomEvent,
  trackRedditOfferView,
  updateRedditUserMatch,
  disableRedditPixel,
  setUserAdminStatus
};